import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import ImageUploader from "./ImageUploader";
import { Save, PhoneCall, Eye, MousePointerClick, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PopupCallbackAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);

  const load = async () => {
    const [{ data }, ls] = await Promise.all([
      api.get("/settings"),
      api.get("/leads", { params: { type: "rappel" } }).catch(() => ({ data: [] })),
    ]);
    setS(data);
    // filtre sur source="popup_retention"
    setLeads((ls.data || []).filter(l => l.source === "popup_retention").slice(0, 20));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Popup enregistré");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const testTrigger = () => {
    sessionStorage.removeItem("msc_callback_popup_seen");
    toast.success("Session remise à zéro — ouvrez le site public dans un autre onglet pour voir le popup.");
  };

  if (!s) return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;

  const slotStats = leads.reduce((acc, l) => {
    const sl = l.meta?.callback_slot || "?";
    acc[sl] = (acc[sl] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="popup-callback-admin">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Marketing & Acquisition</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Popup "être rappelé"</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Capte les visiteurs qui s'apprêtent à quitter le site en leur proposant un rappel personnalisé. Les demandes arrivent dans <strong>Leads</strong> avec le type "rappel".
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={testTrigger} data-testid="reset-popup-session"><Eye className="w-4 h-4 mr-1.5" />Reset session</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)]" data-testid="save-popup">
            <Save className="w-4 h-4 mr-1.5" />{saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </header>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><PhoneCall className="w-4 h-4" />Activation du popup</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Désactivez temporairement pendant vos tests ou opérations commerciales.</div>
          </div>
          <Switch checked={!!s.popup_callback_enabled} onCheckedChange={v => setS({ ...s, popup_callback_enabled: v })} data-testid="popup-enabled" />
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Déclenchement</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs inline-flex items-center gap-1"><Clock className="w-3 h-3" />Délai auto (secondes)</Label>
            <Input type="number" min={0} value={s.popup_callback_delay_seconds ?? 30} onChange={e => setS({ ...s, popup_callback_delay_seconds: +e.target.value })} data-testid="popup-delay" />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">0 = uniquement sur exit-intent. Recommandé : 30–60s.</div>
          </div>
          <div className="space-y-2 pt-2">
            <div className="flex items-center gap-2">
              <Switch checked={!!s.popup_callback_exit_intent} onCheckedChange={v => setS({ ...s, popup_callback_exit_intent: v })} id="exit" />
              <Label htmlFor="exit" className="text-sm inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" />Exit-intent (sortie souris)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={s.popup_callback_one_per_session !== false} onCheckedChange={v => setS({ ...s, popup_callback_one_per_session: v })} id="once" />
              <Label htmlFor="once" className="text-sm">1 affichage max par session</Label>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Contenu</div>
        <div>
          <Label>Titre</Label>
          <Input value={s.popup_callback_title || ""} onChange={e => setS({ ...s, popup_callback_title: e.target.value })} placeholder="Vous avez une question ?" data-testid="popup-title" />
        </div>
        <div>
          <Label>Sous-titre</Label>
          <Textarea rows={2} value={s.popup_callback_subtitle || ""} onChange={e => setS({ ...s, popup_callback_subtitle: e.target.value })} placeholder="Laissez-nous vos coordonnées, nous vous rappelons sous 24h." data-testid="popup-subtitle" />
        </div>
        <div>
          <Label>Image d'en-tête (optionnel)</Label>
          <ImageUploader value={s.popup_callback_image || ""} onChange={v => setS({ ...s, popup_callback_image: v })} />
          <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Sinon un dégradé de la couleur de thème est affiché.</div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Sparkles className="w-4 h-4" />Statistiques — 20 dernières demandes</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-[color:var(--brand-sky)]/10">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Total popup</div>
            <div className="text-2xl font-bold text-[color:var(--brand-navy)]">{leads.length}</div>
          </div>
          <div className="p-3 rounded-lg bg-amber-50">
            <div className="text-[10px] uppercase tracking-wider text-amber-700">Matin</div>
            <div className="text-2xl font-bold text-amber-700">{slotStats.matin || 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-orange-50">
            <div className="text-[10px] uppercase tracking-wider text-orange-700">Après-midi</div>
            <div className="text-2xl font-bold text-orange-700">{slotStats.apres_midi || 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-indigo-50">
            <div className="text-[10px] uppercase tracking-wider text-indigo-700">Fin de journée</div>
            <div className="text-2xl font-bold text-indigo-700">{slotStats.fin_journee || 0}</div>
          </div>
        </div>
        {leads.length > 0 && (
          <div className="pt-2 space-y-1">
            {leads.slice(0, 5).map(l => (
              <div key={l.id} className="flex items-center justify-between text-xs py-1.5 px-2 bg-[hsl(var(--muted))]/40 rounded">
                <span className="font-mono text-[color:var(--brand-navy)]">{l.name} · {l.phone}</span>
                <span className="text-[hsl(var(--muted-foreground))]">{l.meta?.callback_slot || "—"} · {new Date(l.created_at).toLocaleDateString("fr-FR")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
